<template>
  <div class="flex flex-col flex-1">
    <!-- Tier Badge Header -->
    <div
      class="px-4 py-2 flex items-center justify-center gap-1 font-semibold rounded-xl"
      :class="tierHeaderStyles"
    >
      <span v-if="card.tier === 'current'" class="text-strong font-inter"> Your selection </span>
      <span v-if="card.tier === 'better'" class="text-strongest font-inter"> $ Better value </span>
      <span v-if="card.tier === 'best'" class="text-always-light font-inter">
        <span class="text-green-300">$$</span> Best value
      </span>
    </div>

    <!-- Inner Card Content -->
    <div
      class="flex flex-col items-center justify-between p-4 flex-1 rounded-xl"
      :class="innerCardStyles"
    >
      <!-- Top: Amount + Projected Return -->
      <div class="flex flex-col items-center text-center">
        <!-- Invested Amount -->
        <p class="text-sm font-medium text-strongest font-inter">
          Investing {{ formatCurrency(card.principal) }}
          <span v-if="hasAmountIncrease" class="text-sm text-success ml-1 font-normal">
            (+{{ formatCurrency(card.principal - basePrincipal) }})
          </span>
        </p>

        <!-- Projected Return -->
        <div class="flex flex-col items-center">
          <!-- Compounded: paid at maturity -->
          <template v-if="compounded">
            <p class="text-lg font-semibold text-strongest">
              Return {{ formatCurrencyPrecise(maturityValue) }}
            </p>
            <p class="text-sm text-strong">Paid at maturity</p>
          </template>

          <!-- Simple interest: monthly payments -->
          <template v-else>
            <p class="text-lg font-semibold text-strongest mt-1">
              Return {{ formatCurrencyPrecise(monthlyPayment) }}/mo
            </p>
            <p class="text-sm text-strong">
              Total return {{ formatCurrencyPrecise(totalReturnSimple) }}
            </p>
          </template>
        </div>
      </div>

      <!-- Bottom: Term + CTA -->
      <div class="flex flex-col items-center w-full mt-4">
        <p class="text-sm text-strong mb-2">
          {{ formatTerm(card.offering.bond_term) }}
        </p>
        <Button
          @click="$emit('select', card)"
          :variant="card.tier === 'best' ? 'primary-secondary' : 'paper-primary'"
          class="w-full"
        >
          <template #label>
            {{ card.tier === "current" ? "Keep my selection" : "Get this interest rate" }}
          </template>
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { Button } from "@/components";
import {
  monthlyInterestPayment,
  maturityValue as calcMaturityValue,
  totalInterest,
} from "@/utilities";

const props = defineProps({
  card: {
    type: Object,
    required: true,
  },
  basePrincipal: {
    type: Number,
    required: true,
  },
  compounded: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["select"]);

const hasAmountIncrease = computed(() => props.card.principal > props.basePrincipal);

const monthlyPayment = computed(() =>
  monthlyInterestPayment(props.card.principal, props.card.offering.interest_rate)
);

const maturityValue = computed(() =>
  calcMaturityValue(
    props.card.principal,
    props.card.offering.interest_rate,
    props.card.offering.bond_term,
    { isShortTerm: props.card.offering.is_short_term }
  )
);

const totalReturnSimple = computed(() =>
  totalInterest(
    props.card.principal,
    props.card.offering.interest_rate,
    props.card.offering.bond_term,
    { compounded: false }
  )
);

const tierHeaderStyles = computed(() => {
  switch (props.card.tier) {
    case "best":
      return "bg-green-800 text-white";
    case "better":
      return "bg-container-outline text-strongest";
    default:
      return "bg-transparent text-strongest";
  }
});

const innerCardStyles = computed(() => {
  switch (props.card.tier) {
    case "best":
      return "bg-container-bg border-2 border-success";
    default:
      return "bg-container-bg border-t border-container-outline";
  }
});

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatCurrencyPrecise(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatTerm(months) {
  if (months >= 12) {
    const years = months / 12;
    return `${years}-year term`;
  }
  return `${months}-month term`;
}
</script>
